import { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { base64UrlDecode } from './pkce';

/**
 * Gets the username from the current Cognito session.
 * For native users, username is the email.
 * For SSO users, username is in the format {IdPName}_{sub}.
 *
 * @returns Promise<string> The username from the ID token
 * @throws Error if the session cannot be retrieved or token cannot be decoded
 */
export async function getUsernameFromToken(): Promise<string> {
  try {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken().getJwtToken();
    const tokenPayload = JSON.parse(base64UrlDecode(idToken.split('.')[1]));
    return tokenPayload['cognito:username'] || tokenPayload.sub;
  } catch (error) {
    console.error('Failed to get username from token:', error);
    throw new Error('Failed to get username from token');
  }
}

/**
 * Custom hook to get the username from the current Cognito session.
 * Returns the username once it's available, or undefined while loading.
 */
export function useUsername(): string | undefined {
  const [username, setUsername] = useState<string | undefined>(undefined);

  useEffect(() => {
    getUsernameFromToken()
      .then(setUsername)
      .catch((error) => {
        console.error('Failed to get username:', error);
      });
  }, []);

  return username;
}
